import { ReactElement, forwardRef, ForwardedRef, useCallback, useEffect, useRef, useState, RefCallback } from 'react';

import { match } from '@hh.ru/magritte-common-fuzzy-search';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { ItemsList } from '@hh.ru/magritte-ui-tree-selector/ItemsList';
import { MobileItemsList } from '@hh.ru/magritte-ui-tree-selector/MobileItemsList';
import { TreeSelectorWrapper } from '@hh.ru/magritte-ui-tree-selector/TreeSelectorWrapper';
import { filterWithParents } from '@hh.ru/magritte-ui-tree-selector/collection/treeCollectionHelper';
import { AdditionalDefault, TreeModel } from '@hh.ru/magritte-ui-tree-selector/collection/types';
import { ListControls, TreeSelectorDummyProps } from '@hh.ru/magritte-ui-tree-selector/types';
import { useExpanded } from '@hh.ru/magritte-ui-tree-selector/useExpanded';
import { useIndeterminate } from '@hh.ru/magritte-ui-tree-selector/useIndeterminate';
import { useRenderInput } from '@hh.ru/magritte-ui-tree-selector/useRenderInput';

const defaultCheckSelectable = () => true;
const defaultArray: string[] = [];

const needToApply = (query: string): boolean => {
    return !!(query && query.length);
};

export const TreeSelectorDummyComponent = <Additional extends AdditionalDefault>(
    {
        collection: initialCollection,
        checkSelectable = defaultCheckSelectable,
        initialExpanded = defaultArray,
        value: selected,
        leavesOnly,
        singleChoice,
        disabled = defaultArray,
        treeFilter = filterWithParents,
        suggestedNotFound = defaultArray,
        constantlySuggested = defaultArray,
        renderItemForDesktop,
        renderItem,
        renderMobileDelimiter,
        onExpand,
        onChange,
        onChangeFilterQuery,
        contentNotFound,
        onMobileNavigationChange,
        getMobileSearchItemOrder,
        expandTreeOnSelected = true,
        getSelectAllParentTrl,
        renderContentBefore,
        children,
    }: TreeSelectorDummyProps<Additional>,
    ref: ForwardedRef<ListControls>
): ReactElement => {
    const [collection, setCollection] = useState(initialCollection);
    const [isSearch, setIsSearch] = useState(false);
    const currentQuery = useRef('');
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestedNotFoundModels = useRef(initialCollection.getExistModels(suggestedNotFound));
    const constantlySuggestedModels = useRef(initialCollection.getExistModels(constantlySuggested));
    const getSearchItemOrderDefault = useCallback(
        (model: TreeModel<Additional>) => {
            return initialCollection.getModelLevel(model.id);
        },
        [initialCollection]
    );

    const { indeterminate } = useIndeterminate({ collection: initialCollection, selected });

    const {
        expanded: currentExpanded,
        setExpanded,
        handleExpansion,
        handleResetExpanded,
    } = useExpanded({
        initialValue: initialExpanded?.slice(),
        selected,
        expandTreeOnSelected,
        collection: initialCollection,
        onExpand,
    });
    const { isMobile } = useBreakpoint();

    const { contentFilterQuery, setInputValue, handleChangeInput, renderInput } = useRenderInput(inputRef);

    const treeRefCallback: RefCallback<HTMLDivElement> = useCallback(
        (node) => {
            if (!node) {
                handleResetExpanded();
            }
        },
        [handleResetExpanded]
    );

    useEffect(() => {
        const contentFilterQueryTrimmed = contentFilterQuery.trim();
        const queryWasChanged = contentFilterQueryTrimmed !== currentQuery.current.trim();

        if (queryWasChanged && needToApply(contentFilterQueryTrimmed)) {
            const newExpanded: string[] = [];
            const newCollection = treeFilter(
                initialCollection,
                (item) => match(contentFilterQueryTrimmed, item.text),
                isMobile
            );
            newCollection.toList().forEach((item) => {
                if (newCollection.hasChildren(item.id)) {
                    // Если есть в отфильтрованной коллекции есть дочерние элементы,
                    // значит они заматчились, и нужно открыть родителя.
                    newExpanded.push(item.id);
                } else {
                    // Если заматчился только сам родитель, показываем его
                    // схлопнутым => нужно добавить его ветку в коллекцию.
                    initialCollection.walkChildren(item.id, (child, parents) => {
                        newCollection.addModel({ ...child }, parents[0].id);
                    });
                }
            });

            const filteredIds = newCollection.toList().map((model) => model.id);

            // Если в отфильтрованной коллекции нет моделей, но заданы предложенные,
            // то показываем их
            // Если в отфильтрованной коллекции модели есть, то пробуем добавить к ним
            // всегда показывающиеся модели, если их ещё нет в коллекции
            if (!filteredIds.length && suggestedNotFoundModels.current.length) {
                suggestedNotFoundModels.current.forEach((model) => newCollection.addModel({ ...model }));
            } else {
                constantlySuggestedModels.current.forEach((model) => {
                    if (!filteredIds.includes(model.id)) {
                        newCollection.addModel({ ...model });
                    }
                });
            }

            setCollection(newCollection);
            currentQuery.current = contentFilterQuery;
            onChangeFilterQuery?.(filteredIds, contentFilterQueryTrimmed);
            setExpanded(newExpanded);
            setIsSearch(true);
        } else if (queryWasChanged) {
            // Запрос не годится для поиска.
            const newExpanded = initialExpanded.slice();
            setCollection(initialCollection);

            onChangeFilterQuery?.(
                initialCollection.toList().map((model) => model.id),
                contentFilterQueryTrimmed
            );
            currentQuery.current = contentFilterQuery;
            if (inputRef.current) {
                setExpanded(newExpanded);
            } else {
                handleResetExpanded();
            }

            setIsSearch(false);
        }
    }, [
        initialCollection,
        contentFilterQuery,
        treeFilter,
        selected,
        setExpanded,
        isMobile,
        onChangeFilterQuery,
        handleResetExpanded,
        initialExpanded,
    ]);

    const renderTreeSelector = useCallback(() => {
        const hasModels = collection.getTopLevel().length;
        return (
            <TreeSelectorWrapper forwardRef={treeRefCallback}>
                {hasModels ? (
                    <>
                        {isMobile ? (
                            <MobileItemsList
                                getMobileSearchItemOrder={getMobileSearchItemOrder || getSearchItemOrderDefault}
                                collection={collection}
                                selected={selected}
                                disabled={disabled}
                                onChange={onChange}
                                leavesOnly={leavesOnly}
                                checkSelectable={checkSelectable}
                                singleChoice={singleChoice}
                                ref={ref}
                                setInputValue={setInputValue}
                                renderItem={renderItem}
                                renderMobileDelimiter={renderMobileDelimiter}
                                handleChangeInput={handleChangeInput}
                                contentFilterQuery={contentFilterQuery.trim()}
                                onMobileNavigationChange={onMobileNavigationChange}
                                isSearch={isSearch}
                                getSelectAllParentTrl={getSelectAllParentTrl}
                                renderContentBefore={renderContentBefore}
                            />
                        ) : (
                            <ItemsList
                                collection={collection}
                                leavesOnly={leavesOnly}
                                checkSelectable={checkSelectable}
                                expanded={currentExpanded}
                                onExpansion={handleExpansion}
                                selected={selected}
                                onChange={onChange}
                                disabled={disabled}
                                singleChoice={singleChoice}
                                indeterminate={indeterminate}
                                isSearch={isSearch}
                                renderItemForDesktop={renderItemForDesktop}
                                renderContentBefore={renderContentBefore}
                            />
                        )}
                    </>
                ) : (
                    <>{contentNotFound}</>
                )}
            </TreeSelectorWrapper>
        );
    }, [
        collection,
        treeRefCallback,
        isMobile,
        getMobileSearchItemOrder,
        getSearchItemOrderDefault,
        selected,
        disabled,
        onChange,
        leavesOnly,
        checkSelectable,
        singleChoice,
        ref,
        setInputValue,
        renderItem,
        renderMobileDelimiter,
        handleChangeInput,
        contentFilterQuery,
        onMobileNavigationChange,
        isSearch,
        getSelectAllParentTrl,
        renderContentBefore,
        currentExpanded,
        handleExpansion,
        indeterminate,
        renderItemForDesktop,
        contentNotFound,
    ]);

    return <>{children({ renderTreeSelector, renderInput })}</>;
};

export const TreeSelectorDummy = forwardRef(TreeSelectorDummyComponent) as <Additional extends AdditionalDefault>(
    props: TreeSelectorDummyProps<Additional> & { ref?: ForwardedRef<ListControls> }
) => ReactElement;
